import React , { useMemo, useCallback, useEffect, useState, useRef } from 'react'
import { Button } from 'devextreme-react/button'
import ColorBox, { ColorBoxTypes } from 'devextreme-react/color-box'
import ResponsiveBox, { Row, Col, Item, Location } from 'devextreme-react/responsive-box'
import { useSelector } from 'react-redux'
import { useAppDispatch } from 'store'
import { RootState } from 'slices'
import { useTranslation } from 'react-i18next'

import { ColorInput } from 'components/forms/color-input'
import { TextInput } from 'components/forms/text-input'
import { SelectBox } from 'devextreme-react/select-box'

import { NumberInput, SelectInput, CheckBoxInput } from 'components/forms'
import { BaseForm } from 'components'
import { IxConfirmPopup } from 'components'
import { shallowEqual } from '../../helpers/shallow-equal'
import { LookupFormat } from 'helpers/lookup-format-enum'

import { useIxConfirmPopupReducer } from 'hooks'
import { useUserSettingsUpdate } from 'hooks'
import { keyboardControl } from 'helpers'

import { useIxOnScreen } from 'hooks/use-on-screen'
import { Settings } from 'helpers/settings-enum'
import { SettingType } from 'helpers/setting-type-enum'

import { toggleDrawer, setDrawerTabIndex, settingsMenuSelector,} from 'slices/ui'
import { _setLNR } from 'slices/rights'

import { Form, Field, useField } from 'formik'

import { setMaxLabelWidth } from 'helpers/set-max-label-width'

import './settings.scss'

const DEFAULTSETTINGS = {
    lookupThreshold: 20,
    dropDownHeight: 600,
    objNumber: 5,
    unitNumber: 6,
    drawerTabIndex: 0,
    drawerState: 0,
    lookupNumberFormat: LookupFormat.NoFormating,
    lookupSeparatorFormat: LookupFormat.Space,
    lookupInfoFormat: LookupFormat.NoFormating,
    fieldBorderHeight: 1,
    fieldBorderHoverHeight: 1,
    fieldBorderColor: 'none',
    gridHeaderColor: null,
    gridHeaderTextColor: null,
    gridRowColor: null,
    gridRowTextColor: null,
    gridBordersColor: null,
    formFieldType: 'none',
    barsStyles: 'default-bars',
    compact: 'none'
} as any
type SettingsData = typeof DEFAULTSETTINGS

const ResetButton = ({ name, setFieldValue, disabled, showConfirmPopup }: any) => {
    const [{ value }] = useField(name)
    const { t } = useTranslation()

    const setValue = () => {
        setFieldValue(name, DEFAULTSETTINGS[name])
    }

    const onShowConfirmPopup = () => {
        showConfirmPopup({
            title: t('global:label.warning'),
            desc: t('global:question.resetToDefault', { resetItem: `'${ t(`global:field.setting.${name}`) }'` }),
            confirmCb: setValue,
        })
    }

    return (
        <div className='confirm-popup--btns reset-btn' >
            <Button text={t('global:button.reset')} type='normal' stylingMode='contained'  onClick={onShowConfirmPopup} disabled={disabled || value === DEFAULTSETTINGS[name]}/>
        </div>
    )
}

const Buttons = ({ dirty, resetForm, handleSubmit, shallowEqual, onSetSettings, showConfirmPopup}: any) => {
    const { t } = useTranslation()

    const resetFormToDefault = useCallback(() => {
        resetForm({values: DEFAULTSETTINGS})
        onSetSettings(DEFAULTSETTINGS)
    },[resetForm, onSetSettings])

    const onShowConfirmPopupReset = () => {
        showConfirmPopup({
            title: t('global:label.warning'),
            desc: t('global:question.resetToDefault', { resetItem: 'settings' }),
            confirmCb: resetFormToDefault,
        })
    }

    const onShowConfirmPopupDiscard = () => {
        showConfirmPopup({
            title: t('global:label.warning'),
            desc: t('global:question.discardChanges'),
            confirmCb: resetForm,
        })
    }

    return (
        <div className='confirm-popup--btns'>
            <Button text={t('global:button.save')} type='default' disabled={!dirty} stylingMode='contained' onClick={handleSubmit}/>
            <Button text={t('global:button.discardChanges')} type='normal' disabled={!dirty} stylingMode='contained' onClick={onShowConfirmPopupDiscard}/>
            <Button text={t('global:button.resetToDefault')} type='normal' stylingMode='contained' onClick={onShowConfirmPopupReset} disabled={shallowEqual}/>
        </div>
    )
}

const LNRUpdater = () => {
    const dispatch = useAppDispatch()
    const [field] = useField<any>('LNR')
    const firstRender = useRef(true)

    useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false
            return
        }
        dispatch(_setLNR(field.value))
    }, [field.value, dispatch])

    return null
}

export const Setting = () => {
    const dispatch = useAppDispatch()
    const [cPopup, showConfirmPopup] = useIxConfirmPopupReducer()
    const setSettingData = useUserSettingsUpdate()
    const {settings, drawerTabIndex, drawerState} = useSelector((state: RootState) => state.ui)
    
    const {value: lookupThreshold} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.lookupThreshold))
    const {value: dropDownHeight} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.dropDownHeight))
    const {value: objNumber} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.objNumber))
    const {value: unitNumber} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.unitNumber))
    const {value: lookupNumberFormat} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.lookupNumberFormat))
    const {value: lookupSeparatorFormat} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.lookupSeparatorFormat))
    const {value: lookupInfoFormat} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.lookupInfoFormat))
    const {value: barsStyle} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.barsStyle))
    const {value: openLookupOnClick} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.openLookupOnClick))
    const {value: fieldBorderHeight} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.fieldBorderHeight))
    const {value: compact} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.compact))
    const {value: fieldBorderHoverHeight} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.fieldBorderHoverHeight))
    const {value: fieldBorderColor} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.fieldBorderColor))
    const {value: formFieldType} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.formFieldType))
    const {value: gridHeaderColor} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.gridHeaderColor))
    const {value: gridHeaderTextColor} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.gridHeaderTextColor))
    const {value: gridRowColor} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.gridRowColor))
    const {value: gridRowTextColor} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.gridRowTextColor))
    const {value: gridBordersColor} = useSelector((state: RootState) => settingsMenuSelector(state, Settings.gridBordersColor))


    const { LNR, statusLNR } = useSelector((state: RootState) => state.rights)
    const isDevLicense = useMemo(() => statusLNR === 'loaded' && LNR === 999999, [LNR, statusLNR])

    const { t } = useTranslation()
    const nextElementIndex: any = useRef({})
    const ref = useRef<any>(null)
    const onScreen = useIxOnScreen(ref)

    useEffect(() => {
        if (onScreen) {
            const formElement: any = ref.current

            if (formFieldType === 'label-inside-max-field-width' || formFieldType === 'label-left-max-field-width') {
                setTimeout(() => {
                    setMaxLabelWidth(formElement)
                }, 500)
            }
            else {
                formElement?.style.removeProperty('--max-labels-width')
            }
        }

    },[onScreen, formFieldType])

    const itemsData = useMemo(() => ([
        {name: 'lookupThreshold', index: 1, type: 'field'},
        {name: t('global:button.reset'), index: 0, type: 'button'},
        {name: 'dropDownHeight', index: 1, type: 'field'},
        {name: t('global:button.reset'), index: 1, type: 'button'},
        {name: 'objNumber', index: 1, type: 'field'},
        {name: t('global:button.reset'), index: 2, type: 'button'},
        {name: 'unitNumber', index: 1, type: 'field'},
        {name: t('global:button.reset'), index: 3, type: 'button'},
        {name: 'drawerState', index: 1, type: 'field'},
        {name: t('global:button.reset'), index: 4, type: 'button'},
        {name: 'drawerTabIndex', index: 1, type: 'field'},
        {name: t('global:button.reset'), index: 5, type: 'button'},
        {name: 'lookupNumberFormat', index: 1, type: 'field'},
        {name: t('global:button.reset'), index: 6, type: 'button'},
        {name: 'lookupSeparatorFormat', index: 1, type: 'field'},
        {name: t('global:button.reset'), index: 7, type: 'button'},
        {name: 'lookupInfoFormat', index: 1, type: 'field'},
        {name: t('global:button.reset'), index: 8, type: 'button'},
        {name: 'fieldBorderHeight', index: 1, type: 'field'},
        {name: t('global:button.reset'), index: 9, type: 'button'},
        {name: 'fieldBorderHoverHeight', index: 1, type: 'field'},
        {name: t('global:button.reset'), index: 10, type: 'button'},
        {name: 'fieldBorderColor', index: 1, type: 'field'},
        {name: t('global:button.reset'), index: 11, type: 'button'},

        {name: 'formFieldType', index: 1, type: 'field'},
        {name: t('global:button.reset'), index: 12, type: 'button'},

        {name: 'compact', index: 1, type: 'field'},
        {name: t('global:button.reset'), index: 13, type: 'button'},

        {name: 'gridHeaderColor', index: 0, type: 'field'},
        {name: t('global:button.reset'), index: 14, type: 'button'},
        {name: 'gridHeaderTextColor', index: 0, type: 'field'},
        {name: t('global:button.reset'), index: 15, type: 'button'},
        {name: 'gridRowColor', index: 0, type: 'field'},
        {name: t('global:button.reset'), index: 16, type: 'button'},
        {name: 'gridRowTextColor', index: 0, type: 'field'},
        {name: t('global:button.reset'), index: 17, type: 'button'},
        {name: 'gridBordersColor', index: 0, type: 'field'},
        {name: t('global:button.reset'), index: 18, type: 'button'},

        {name: t('global:button.save'), index: 0, type: 'button'},
        {name: t('global:button.discardChanges'), index: 0, type: 'button'},
        {name: t('global:button.resetToDefault'), index: 0, type: 'button'},
    ]),[t])

    const onKeyboardControl = keyboardControl(itemsData, t, ref)

    useEffect(() => {
        const items: any = itemsData.map((item: any) => item.type === 'field' ? document.getElementsByName(item.name)[item.index] : document.querySelectorAll(`[aria-label="${item.name}"]`)[item.index])
        const elementForFocus: any = items.indexOf(document.activeElement) + 1

        if (cPopup.visible) {
            nextElementIndex.current = elementForFocus
        }
        else {
            setTimeout(() => {
                items[items.length === nextElementIndex.current ? 0 : nextElementIndex.current]?.focus()
            }, 130)

        }
    },[cPopup.visible, itemsData])

    const onSetSettings = useCallback((values: SettingsData) => {
            document.querySelector('body')?.classList.remove(`field-border-${fieldBorderHeight}px`)
            document.querySelector('body')?.classList.remove(`field-border-hover-${fieldBorderHoverHeight}px`)
            document.querySelector('body')?.classList.remove(`field-border-${fieldBorderColor}`)
            document.querySelector('body')?.classList.remove(`form-field-${formFieldType}`)
            document.querySelector('body')?.classList.remove(`form-field-${compact}`)

            Object.keys(values).forEach((settingName: any) => {
                const setting: any = settings.find((setting: any) => setting.settingName === settingName)
                setting && setSettingData('ui-config', settingName, values[settingName], setting.id, SettingType.GlobalSetting)
            })

            dispatch(setDrawerTabIndex(values.drawerTabIndex))
            dispatch(toggleDrawer(values.drawerState))

    },[dispatch, setSettingData, fieldBorderHeight, fieldBorderHoverHeight, fieldBorderColor, formFieldType, compact, settings])

    const OPTIONS = useMemo(() => ({
        lookupThreshold: [
            {name: '10', value: 10},
            {name: `20 (${t('global:label.default')})`, value: DEFAULTSETTINGS.lookupThreshold},
            {name: '30', value: 30},
            {name: t('global:label.alwaysAsLookup'), value: -1},
            {name: t('global:label.alwaysAsDropdown'), value: 1000000}
        ],
        drawerTabIndex: [
            {name: '0 (default)', value: DEFAULTSETTINGS.drawerTabIndex},
            {name: '1', value: 1},
            {name: '2', value: 2}
        ],
        dropDownHeight: [
            {name: t('global:label.small'), value: 300},
            {name: t('global:label.middle'), value: 450},
            {name: `${t('global:label.large')} (${t('global:label.default')})`, value: DEFAULTSETTINGS.dropDownHeight}
        ],
        objNumber: [
            {name: '4', value: 4},
            {name: `5 (${t('global:label.default')})`, value: DEFAULTSETTINGS.objNumber},
            {name: '6', value: 6}
        ],
        unitNumber: [
            {name: '4', value: 4},
            {name: '5', value: 5},
            {name: `6 (${t('global:label.default')})`, value: DEFAULTSETTINGS.unitNumber}
        ],
        drawerState: [
            {name: `0 (${t('global:label.default')})`, value: DEFAULTSETTINGS.drawerState},
            {name: '1', value: 1},
            {name: '2', value: 2}
        ],
        lookupNumberFormat: [
            {name: `${t('global:label.noFormating')} (${t('global:label.default')})`, value: DEFAULTSETTINGS.lookupNumberFormat},
            {name: `${t('global:label.squareBracket')}`, value: LookupFormat.SquareBracket},
            {name: `${t('global:label.curlyBracket')}`, value: LookupFormat.CurlyBracket}
        ],
        lookupSeparatorFormat: [
            {name: `${t('global:label.space')} (${t('global:label.default')})`, value: DEFAULTSETTINGS.lookupSeparatorFormat},
            {name: `${t('global:label.dash')}`, value: LookupFormat.Dash},
            {name: `${t('global:label.pipe')}`, value: LookupFormat.Pipe},
            {name: `${t('global:label.doublePipe')}`, value: LookupFormat.DoublePipe},
            {name: `${t('global:label.slash')}`,value: LookupFormat.Slash},
            {name: `${t('global:label.doubleSlash')}`, value: LookupFormat.DoubleSlash}
        ],
        lookupInfoFormat: [
            {name: `${t('global:label.noFormating')}  (${t('global:label.default')})`, value: DEFAULTSETTINGS.lookupInfoFormat},
            {name: `${t('global:label.squareBracket')}`, value: LookupFormat.SquareBracket},
            {name: `${t('global:label.curlyBracket')}`, value: LookupFormat.CurlyBracket}
        ],
        fieldBorderHeight: [
            {name: `1px (${t('global:label.default')})`, value: DEFAULTSETTINGS.fieldBorderHeight},
            {name: '2px', value: 2},
        ],
        fieldBorderColor: [
            {name: `${t('global:label.default')}`, value: DEFAULTSETTINGS.fieldBorderColor},
            {name: 'green', value: 'ix-field-border-color-1'},
            {name: 'yellow', value: 'ix-field-border-color-2'},
        ],
        fieldBorderHoverHeight: [
            {name: `1px (${t('global:label.default')})`, value: DEFAULTSETTINGS.fieldBorderHoverHeight},
            {name: '2px', value: 2},
            {name: '3px', value: 3},
        ],
        barsStyles: [
            { name: `${t('global:label.defaultBars')}`, value: DEFAULTSETTINGS.barsStyles },
            { name: `${t('global:label.roundedBars')}`, value: 'rounded-bars' },
            { name: `${t('global:label.superRoundedBars')}`, value: 'super-rounded-bars' },
        ],
        formFieldType: [
            { name: `${t('global:label.default')}`, value: DEFAULTSETTINGS.formFieldType},
            { name: `${t('global:label.labelInside')}`, value: 'label-inside'},
            { name: `${t('global:label.labelInsideFixedFieldWidth')}`, value: 'label-inside-fixed-field-width'},
            { name: `${t('global:label.labelInsideMaxFieldWidth')}`, value: 'label-inside-max-field-width'},
            { name: `${t('global:label.labelLeftSingleLine')}`, value: 'label-left-single-line'},
            { name: `${t('global:label.labelLeftMaxFieldWidth')}`, value: 'label-left-max-field-width'},
            { name: `${t('global:label.labelLeftMultiLine')}`, value: 'label-left-multi-linel'},
            { name: `${t('global:label.floatingLabel')}`, value: 'floating-label'},
        ],
        compact: [
            { name: `${t('global:label.default')}`, value: DEFAULTSETTINGS.compact },
            { name: `${t('global:label.compact')}`, value: 'compact' },
            { name: `${t('global:label.compactV2')}`, value: 'compact-v2' },
            { name: `${t('global:label.compactV3')}`, value: 'compact-v3' },
            { name: `${t('global:label.compactDefaultValueSize')}`, value: 'compact-default-value-size'},
        ],
    }),[t])

    const _initialValues = useMemo(() => ({
        lookupThreshold: lookupThreshold,
        dropDownHeight: dropDownHeight,
        objNumber: objNumber,
        unitNumber: unitNumber,
        drawerTabIndex: drawerTabIndex,
        drawerState: drawerState,
        lookupNumberFormat: (lookupNumberFormat as any)?.typeFormat,
        lookupSeparatorFormat: (lookupSeparatorFormat as any)?.typeFormat,
        lookupInfoFormat: (lookupInfoFormat as any)?.typeFormat,
        fieldBorderHeight: fieldBorderHeight,
        fieldBorderColor: fieldBorderColor,
        fieldBorderHoverHeight: fieldBorderHoverHeight,
        gridHeaderColor: gridHeaderColor,
        gridHeaderTextColor: gridHeaderTextColor,
        gridRowColor: gridRowColor,
        gridRowTextColor: gridRowTextColor,
        gridBordersColor: gridBordersColor,
        barsStyle: barsStyle,
        formFieldType: formFieldType,
        compact: compact,
        openLookupOnClick: openLookupOnClick,

    }),[lookupThreshold, dropDownHeight, gridHeaderTextColor, drawerTabIndex, drawerState, gridRowColor, gridRowTextColor, compact, openLookupOnClick, objNumber, unitNumber,
        gridBordersColor, fieldBorderHeight, fieldBorderColor, fieldBorderHoverHeight, gridHeaderColor, barsStyle, formFieldType, lookupInfoFormat, lookupNumberFormat, lookupSeparatorFormat])


    const formik = useMemo(() => ({
        initialValues: _initialValues,
        enableReinitialize: true,
        jsonSchema: null,
        avjOptions: {
            coerceTypes: true,
            $data: true,
        },
        onSubmit: async (values: any) => {
            const { LNR, ..._values } = values  // eslint-disable-line @typescript-eslint/no-unused-vars
            onSetSettings(_values)
        },
    }),[onSetSettings, _initialValues])
    
    return (
        <div className='form' ref={ref} {...onScreen && {onKeyDown: onKeyboardControl}}>
            <BaseForm {...formik}>
                {({ handleSubmit, dirty, resetForm, setFieldValue, values }: any) => (
                    <Form id='settings-form'>
                        <ResponsiveBox>
                            <Row />
                            <Row />
                            <Row />
                            <Row />
                            <Row />
                            <Row />
                            <Row />
                            <Row />
                            <Row />
                            <Row />
                            <Row />
                            <Row />
                            <Row />
                            <Row />
                            <Row />
                            <Row />
                            <Row />
                            <Row />
                            <Row />
                            <Row />
                            <Row />
                            <Row />
                            <Row />
                            <Row />

                            <Col ratio={ 1 }/>
                            <Col ratio={ 0 } baseSize={'auto'}/>

                            <Item>
                                <Location row={0} col={0} />
                                <Field
                                    as={SelectInput}
                                    name='lookupThreshold'
                                    opts={OPTIONS.lookupThreshold}
                                    label={t('global:field.setting.lookupThreshold')}
                                />
                            </Item>
                            <Item>
                                <Location row={0} col={1} />
                                <ResetButton name='lookupThreshold' setFieldValue={setFieldValue} showConfirmPopup={showConfirmPopup}/>
                            </Item>

                            <Item>
                                <Location row={1} col={0} />
                                <Field
                                    as={SelectInput}
                                    name='dropDownHeight'
                                    opts={OPTIONS.dropDownHeight}
                                    label={t('global:field.setting.dropDownHeight')}
                                    readOnly={values.lookupThreshold === -1}
                                />

                            </Item>
                            <Item>
                                <Location row={1} col={1} />
                                <ResetButton name='dropDownHeight' setFieldValue={setFieldValue} disabled={values.lookupThreshold === -1 || values.lookupThreshold === DEFAULTSETTINGS.dropDownHeight} showConfirmPopup={showConfirmPopup}/>
                            </Item>

                            {isDevLicense ? (
							<Item>
                                <Location row={2} col={0} />
                                <Field
                                    as={SelectInput}
                                    name='objNumber'
                                    opts={OPTIONS.objNumber}
                                    label={t('global:field.setting.objNumber')}
                                />
                            </Item>
							) : null}
                            {isDevLicense ? (
                            <Item>
                                <Location row={2} col={1} />
                                <ResetButton name='objNumber' setFieldValue={setFieldValue} showConfirmPopup={showConfirmPopup}/>
                            </Item>
							) : null}

                            {isDevLicense ? (
                            <Item>
                                <Location row={3} col={0} />
                                <Field
                                    as={SelectInput}
                                    name='unitNumber'
                                    opts={OPTIONS.unitNumber}
                                    label={t('global:field.setting.unitNumber')}
                                />
                            </Item>
							) : null}
                            {isDevLicense ? (
                            <Item>
                                <Location row={3} col={1} />
                                <ResetButton name='unitNumber' setFieldValue={setFieldValue} showConfirmPopup={showConfirmPopup}/>
                            </Item>
							) : null}

                            {isDevLicense ? (
                            <Item>
                                <Location row={4} col={0} />
                                <Field
                                    as={SelectInput}
                                    name='drawerState'
                                    opts={OPTIONS.drawerState}
                                    label={t('global:field.setting.drawerState')}
                                />
                            </Item>
							) : null}
                            {isDevLicense ? (
                            <Item>
                                <Location row={4} col={1} />
                                <ResetButton name='drawerState' setFieldValue={setFieldValue} showConfirmPopup={showConfirmPopup}/>
                            </Item>
							) : null}

                            {isDevLicense ? (
                            <Item>
                                <Location row={5} col={0} />
                                <Field
                                    as={SelectInput}
                                    name='drawerTabIndex'
                                    opts={OPTIONS.drawerTabIndex}
                                    label={t('global:field.setting.drawerTabIndex')}
                                />

                            </Item>
							) : null}
                            {isDevLicense ? (
                            <Item>
                                <Location row={5} col={1} />
                                <ResetButton name='drawerTabIndex' setFieldValue={setFieldValue} showConfirmPopup={showConfirmPopup}/>

                            </Item>
							) : null}

                            <Item>
                                <Location row={6} col={0} />
                                <Field
                                    as={SelectInput}
                                    name='lookupNumberFormat'
                                    opts={OPTIONS.lookupNumberFormat}
                                    label={t('global:field.setting.lookupNumberFormat')}
                                />

                            </Item>
                            <Item>
                                <Location row={6} col={1} />
                                <ResetButton name='lookupNumberFormat' setFieldValue={setFieldValue} showConfirmPopup={showConfirmPopup}/>

                            </Item>

                            <Item>
                                <Location row={7} col={0} />
                                <Field
                                    as={SelectInput}
                                    name='lookupSeparatorFormat'
                                    opts={OPTIONS.lookupSeparatorFormat}
                                    label={t('global:field.setting.lookupSeparatorFormat')}
                                />

                            </Item>
                            <Item>
                                <Location row={7} col={1} />
                                <ResetButton name='lookupSeparatorFormat' setFieldValue={setFieldValue} showConfirmPopup={showConfirmPopup}/>

                            </Item>

                            <Item>
                                <Location row={8} col={0} />
                                <Field
                                    as={SelectInput}
                                    name='lookupInfoFormat'
                                    opts={OPTIONS.lookupInfoFormat}
                                    label={t('global:field.setting.lookupInfoFormat')}
                                />

                            </Item>
                            <Item>
                                <Location row={8} col={1} />
                                <ResetButton name='lookupInfoFormat' setFieldValue={setFieldValue} showConfirmPopup={showConfirmPopup}/>

                            </Item>

                            <Item>
                                <Location row={9} col={0} />
                                <Field
                                    as={SelectInput}
                                    name='fieldBorderHeight'
                                    opts={OPTIONS.fieldBorderHeight}
                                    label={t('global:field.setting.fieldBorderHeight')}
                                />

                            </Item>
                            <Item>
                                <Location row={9} col={1} />
                                <ResetButton name='fieldBorderHeight' setFieldValue={setFieldValue} showConfirmPopup={showConfirmPopup}/>

                            </Item>

                            <Item>
                                <Location row={10} col={0} />
                                <Field
                                    as={SelectInput}
                                    name='fieldBorderHoverHeight'
                                    opts={OPTIONS.fieldBorderHoverHeight}
                                    label={t('global:field.setting.fieldBorderHoverHeight')}
                                />

                            </Item>
                            <Item>
                                <Location row={10} col={1} />
                                <ResetButton name='fieldBorderHoverHeight' setFieldValue={setFieldValue} showConfirmPopup={showConfirmPopup}/>

                            </Item>

                            {isDevLicense ? (
                            <Item>
                                <Location row={11} col={0} />
                                <Field
                                    as={SelectInput}
                                    name='fieldBorderColor'
                                    opts={OPTIONS.fieldBorderColor}
                                    label={t('global:field.setting.fieldBorderColor')}
                                />

                            </Item>
							) : null}
                            {isDevLicense ? (
                            <Item>
                                <Location row={11} col={1} />
                                <ResetButton name='fieldBorderColor' setFieldValue={setFieldValue} showConfirmPopup={showConfirmPopup}/>

                            </Item>
							) : null}

                            <Item>
                                <Location row={12} col={0} />
                                <Field
                                    as={SelectInput}
                                    name='formFieldType'
                                    opts={OPTIONS.formFieldType}
                                    label={t('global:field.setting.formFieldType')}
                                />

                            </Item>
                            <Item>
                                <Location row={12} col={1} />
                                <ResetButton name='formFieldType' setFieldValue={setFieldValue} showConfirmPopup={showConfirmPopup}/>

                            </Item>

                            <Item>
                                <Location row={13} col={0} />
                                <Field
                                    as={SelectInput}
                                    name='compact'
                                    opts={OPTIONS.compact}
                                    label={t('global:field.setting.compact')}
                                />

                            </Item>
                            <Item>
                                <Location row={13} col={1} />
                                <ResetButton name='compact' setFieldValue={setFieldValue} showConfirmPopup={showConfirmPopup}/>

                            </Item>

                            {isDevLicense ? (
                            <Item>
                                <Location row={14} col={0} />
                                <Field
                                    as={ColorInput}
                                    name='gridHeaderColor'
                                    label={t('global:field.setting.gridHeaderColor')}
                                />

                            </Item>
							) : null}
                            {isDevLicense ? (
                            <Item>
                                <Location row={14} col={1} />
                                <ResetButton name='gridHeaderColor' setFieldValue={setFieldValue} showConfirmPopup={showConfirmPopup}/>

                            </Item>
							) : null}

                            {isDevLicense ? (
                            <Item>
                                <Location row={15} col={0} />
                                <Field
                                    as={ColorInput}
                                    name='gridHeaderTextColor'
                                    label={t('global:field.setting.gridHeaderTextColor')}
                                />

                            </Item>
							) : null}
                            {isDevLicense ? (
                            <Item>
                                <Location row={15} col={1} />
                                <ResetButton name='gridHeaderTextColor' setFieldValue={setFieldValue} showConfirmPopup={showConfirmPopup}/>

                            </Item>
							) : null}

                            {isDevLicense ? (
                            <Item>
                                <Location row={16} col={0} />
                                <Field
                                    as={ColorInput}
                                    name='gridRowColor'
                                    label={t('global:field.setting.gridRowColor')}
                                />

                            </Item>
							) : null}
                            {isDevLicense ? (
                            <Item>
                                <Location row={16} col={1} />
                                <ResetButton name='gridRowColor' setFieldValue={setFieldValue} showConfirmPopup={showConfirmPopup}/>

                            </Item>
							) : null}

                            {isDevLicense ? (
                            <Item>
                                <Location row={17} col={0} />
                                <Field
                                    as={ColorInput}
                                    name='gridRowTextColor'
                                    label={t('global:field.setting.gridRowTextColor')}
                                />

                            </Item>
							) : null}
                            {isDevLicense ? (
                            <Item>
                                <Location row={17} col={1} />
                                <ResetButton name='gridRowTextColor' setFieldValue={setFieldValue} showConfirmPopup={showConfirmPopup}/>

                            </Item>
							) : null}

                            {isDevLicense ? (
                            <Item>
                                <Location row={18} col={0} />
                                <Field
                                    as={ColorInput}
                                    name='gridBordersColor'
                                    label={t('global:field.setting.gridBordersColor')}
                                />

                            </Item>
							) : null}
                            {isDevLicense ? (
                            <Item>
                                <Location row={18} col={1} />
                                <ResetButton name='gridBordersColor' setFieldValue={setFieldValue} showConfirmPopup={showConfirmPopup}/>
                            </Item>
							) : null}

                            <Item>
                                <Location row={19} col={0} />
                                <Field
                                    as={SelectInput}
                                    name='barsStyle'
                                    opts={OPTIONS.barsStyles}
                                    label={t('global:field.setting.barsStyle')}
                                />

                            </Item>
                            <Item>
                                <Location row={19} col={1} />
                                <ResetButton name='barsStyle' setFieldValue={setFieldValue} showConfirmPopup={showConfirmPopup}/>

                            </Item>

                            <Item>
                                <Location row={20} col={0} />
                                <Field
                                    as={CheckBoxInput}
                                    name='openLookupOnClick'
                                    // opts={OPTIONS.barsStyles}
                                    label={t('global:field.setting.openLookupOnClick')}
                                />

                            </Item>
                            {
                                isDevLicense ? (
                                    <Item>
                                        <Location row={21} col={0} />

                                        <Field
                                            as={NumberInput}
                                            name='LNR'
                                            label='LNR'
                                        />
                                    </Item>
                                ) : null
                            }

                            <Item>
                                <Location row={22} col={0} />
                                <Buttons dirty={dirty} handleSubmit={handleSubmit} resetForm={resetForm} shallowEqual={shallowEqual(values, DEFAULTSETTINGS)} onSetSettings={onSetSettings} showConfirmPopup={showConfirmPopup}/>
                            </Item>
                        </ResponsiveBox>
                        <LNRUpdater />
                    </Form>
                )}
            </BaseForm>

            <IxConfirmPopup
                popupVisible={ cPopup.visible }
                title={ cPopup.title }
                description={ cPopup.desc }
                onConfirm={ cPopup.confirmCb }
                onHiding={ cPopup.rejectCb }
            />
        </div>
    )
}
